import React, { useState, useEffect, useMemo, useCallback } from "react";
import { VectorMap } from "@react-jvectormap/core";
import { worldMill } from "@react-jvectormap/world";
import { Formik, Form, Field, ErrorMessage } from "formik";
import '../../stylesheets/Reports.css'
import '../../stylesheets/UserManagement.css'
import '../../stylesheets/History.css'
import Toolbar from '../Toolbar';
import Sidebar from '../Sidebar';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { useNavigate } from "react-router-dom";
import { HistoryAPI } from "../../axios";

const MapComponent = () => {
  const navigate = useNavigate();

  const [filteredArray, setFilteredArray] = useState([]);
  const [message, setMessage] = useState("");
  const [showChart, setShowChart] = useState(true);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch and set country list for Select component
  useEffect(() => {
    const fullCountryList = countryList().getData().map(country => ({
      label: country.label,
      value: country.label,
    }));
    setCountries(fullCountryList);
  }, []);

  // Fetch data on component mount or when form values change
  const fetchData = useCallback((location, customDate) => {
    setLoading(true);
    HistoryAPI.fetch_data_history(location, customDate, null)
      .then((res) => {
        setFilteredArray(res.data.filteredWastesForFirstColumn);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData('Pakistan', '2024-09-02');
  }, [fetchData]);

  const handleSubmit = (values) => {
    fetchData(values.location, values.customDate);
  };

  const handleFieldChange = (handleChange, setFieldValue, submitForm, values) => (e) => {
    handleChange(e);
    const { name, value } = e.target;
    setFieldValue(name, value);
    if (values.location && values.customDate) {
      submitForm();
    }
  };

  useEffect(() => {
    if (filteredArray.length === 0) {
      setMessage("There is no records available for these parameters");
      setShowChart(false);
    } else {
      setMessage("");
      setShowChart(true);
    }
  }, [filteredArray]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      border: 'none',
      boxShadow: 'none',
      minHeight: 'auto',
      height: '100%',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'gray',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'black',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      color: 'black',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: '200px',
      overflowY: 'scroll',
      scrollbarWidth: 'thin',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#ccc' : 'white',
      color: 'black',
      '&:hover': {
        backgroundColor: '#eee',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  };

  const markers = useMemo(() => {
    return filteredArray.map(({ country, latitude, longitude }) => ({
      latLng: [latitude, longitude],
      name: country,
    }));
  }, [filteredArray]);

  return (
    <div className="main-container">
      <Toolbar />
      <Sidebar />
      <div className="content-container">
        <div className="main-content">
          <div className="management-header">
            <h3> Reports </h3>
          </div>
          <div className="manage-headings">
            <span
              style={{
                textDecoration: 'none',
                textDecorationColor: 'transparent',
                textDecorationThickness: 'auto',
                textUnderlineOffset: '0px',
                fontWeight: '400',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/pie-chart');
              }}
            >
              Waste Composition
            </span>
            <span
              style={{
                textDecoration: 'none',
                textDecorationColor: 'transparent',
                textDecorationThickness: 'auto',
                textUnderlineOffset: '0px',
                fontWeight: '400',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/column-chart');
              }}
            >
              Waste Volume
            </span>
            <span
              style={{
                textDecoration: 'underline',
                textDecorationColor: '#8F9BC5',
                textDecorationThickness: '1.5px',
                textUnderlineOffset: '4px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate('/map-chart');
              }}
            >
              Real-time Detection
            </span>
          </div>
          <Formik
            initialValues={{ location: 'Pakistan', customDate: '2024-09-02' }}
            onSubmit={handleSubmit}
          >
            {({ handleChange, handleBlur, values, setFieldValue, submitForm }) => (
              <Form>
                <div className="history-buttons" style={{ justifyContent: 'flex-end' }}>
                  <Select
                    name="location"
                    className="history-button"
                    options={countries}
                    onChange={(selectedOption) => {
                      setFieldValue('location', selectedOption.value);
                      submitForm();
                    }}
                    onBlur={handleBlur}
                    value={values.location ? countries.find(option => option.value === values.location) : null}
                    placeholder="Enter location"
                    styles={customStyles}
                  />
                  <ErrorMessage name="location" component="div" className="error" />
                  <Field
                    name="customDate"
                    type="date"
                    className="history-button"
                    onChange={handleFieldChange(handleChange, setFieldValue, submitForm, values)}
                    onBlur={handleBlur}
                    value={values.customDate}
                  />
                  <ErrorMessage name="customDate" component="div" className="error" />
                </div>
              </Form>
            )}
          </Formik>
          {showChart ? (
            !loading && filteredArray.length > 0 && (
              <div style={{ margin: "auto", width: "700px", height: "600px" }}>
                <VectorMap
                  map={worldMill}
                  containerStyle={{ width: "700px", height: "500px" }}
                  backgroundColor="#fff"
                  regionStyle={{ initial: { fill: "#E0E0E0" } }}
                  markers={markers}
                  markerStyle={{
                    initial: {
                      fill: "#FF0000",
                      stroke: "#FFFFFF",
                      "stroke-width": 0.2,
                      "stroke-opacity": 1,
                      r: 3,
                    },
                  }}
                />
              </div>
            )
          ) : (
            <h2 style={{ display: 'flex', justifyContent: 'center' }}> {message} </h2>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapComponent;
